import { useIsLaptop } from '@synoptic/ui-kit/responsive/hooks.js';
import { ReactNode, FC, useState, useMemo } from 'react';
import { useMatch } from 'react-router';
import { paths } from '@/routes-utils/paths';
import { getPersistedState, persistState } from '@/state-utils/persisted-state';
import { SidebarContextProvider } from './sidebar-context';

enum SidebarState {
  Expanded = 'expanded',
  Collapsed = 'collapsed',
}

const getInitialCollapsed = (key: string, defaultState: SidebarState) =>
  getPersistedState(key, SidebarState, defaultState) === SidebarState.Collapsed;

const persistCollapsed = (key: string, collapsed: boolean) =>
  persistState(key, collapsed ? SidebarState.Collapsed : SidebarState.Expanded);

export const SidebarProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() =>
    getInitialCollapsed('sidebar-state', SidebarState.Expanded),
  );
  const [isTerminalCollapsed, setIsTerminalCollapsed] = useState(() =>
    getInitialCollapsed('terminal-sidebar-state', SidebarState.Collapsed),
  );
  const isLaptop = useIsLaptop();
  const isTerminal = Boolean(useMatch(`${paths.terminal}/*`));

  const contextValue = useMemo(() => {
    // On terminal the sidebar is expanded only when both the global and the
    // terminal-specific states are expanded.
    const isVisiblyCollapsed =
      isCollapsed || (isTerminal && isTerminalCollapsed);

    return {
      isCollapsed: isVisiblyCollapsed && isLaptop,
      setIsCollapsed: (value: boolean | ((prev: boolean) => boolean)) => {
        const next =
          typeof value === 'function' ? value(isVisiblyCollapsed) : value;
        if (isTerminal) {
          setIsTerminalCollapsed(next);
          persistCollapsed('terminal-sidebar-state', next);
          if (!next) {
            setIsCollapsed(false);
            persistCollapsed('sidebar-state', false);
          }
        } else {
          setIsCollapsed(next);
          persistCollapsed('sidebar-state', next);
        }
      },
    };
  }, [isCollapsed, isTerminalCollapsed, isTerminal, isLaptop]);

  return (
    <SidebarContextProvider value={contextValue}>
      {children}
    </SidebarContextProvider>
  );
};
